import type { Identifier } from "@medplum/fhirtypes";

/**
 * Partner-supplied source identities (API v2 `external_refs`), stored as FHIR identifiers.
 *
 * One `{ system, id }` pair becomes `{ system: <prefix><system>, value: <id> }`. Appointments and
 * patients share the prefix, so one source system (`boulevard`) reads the same on both resources,
 * and a patient migration that stamps the reference writes exactly what the API matches on.
 */

export const EXTERNAL_REFERENCE_IDENTIFIER_PREFIX =
  "https://ultralighthealth.com/fhir/external-ref/";

export interface ExternalReference {
  system: string;
  id: string;
}

const FHIR_TOKEN_DELIMITERS = /[\\|,$]/g;

/** The identifier system for one source, e.g. `…/external-ref/boulevard`. */
export function externalReferenceSystem(system: string): string {
  return `${EXTERNAL_REFERENCE_IDENTIFIER_PREFIX}${system}`;
}

export function toExternalReferenceIdentifier(ref: ExternalReference): Identifier {
  return { system: externalReferenceSystem(ref.system), value: ref.id };
}

/** `identifier` search token, with FHIR token grammar escaped before the query layer URL-encodes. */
export function externalReferenceToken(ref: ExternalReference): string {
  return `${externalReferenceSystem(ref.system)}|${ref.id.replace(FHIR_TOKEN_DELIMITERS, "\\$&")}`;
}

/** Every external reference a resource carries, in stored order. */
export function readExternalReferences(identifiers: Identifier[] | undefined): ExternalReference[] {
  return (identifiers ?? []).flatMap((identifier) => {
    if (!identifier.system?.startsWith(EXTERNAL_REFERENCE_IDENTIFIER_PREFIX) || !identifier.value) {
      return [];
    }
    return [
      {
        system: identifier.system.slice(EXTERNAL_REFERENCE_IDENTIFIER_PREFIX.length),
        id: identifier.value,
      },
    ];
  });
}

/**
 * The requested references a resource does not store yet, each once. A repeat inside one request
 * is one reference, so it is appended once.
 */
export function newExternalReferences(
  stored: ExternalReference[],
  requested: ExternalReference[] | undefined,
): ExternalReference[] {
  const isSame = (a: ExternalReference, b: ExternalReference) =>
    a.system === b.system && a.id === b.id;
  return (requested ?? []).filter(
    (ref, index, all) =>
      !stored.some((entry) => isSame(entry, ref)) &&
      all.findIndex((other) => isSame(other, ref)) === index,
  );
}

/** One external reference matched more than one resource. A data-integrity fault, never a pick. */
export class ExternalReferenceAmbiguousError extends Error {
  constructor(readonly resourceIds: string[]) {
    super("External reference matched more than one resource");
    this.name = "ExternalReferenceAmbiguousError";
  }
}
